import React, { memo, useRef } from 'react'
import styled from 'styled-components'
import useClickAway from 'react-use/lib/useClickAway'
import useKey from 'react-use/lib/useKey'
import Card from '../card'

export type Functionality = {
  title: string
  run: () => void | Promise<void>
  isDisabled?: boolean
}

const ContextMenuWrapper = styled(Card)`
  position: absolute;
  padding: 5px;
  z-index: 10;

  div.relative {
    position: relative;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      padding-bottom: 3px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 3px;

      li {
        padding: 0;
        margin: 0;

        a {
          padding: 3px 35px 3px 10px;
          cursor: pointer;
          background-color: transparent;
          border-radius: ${({ theme }) => theme.borderRadius};
          display: block;
          white-space: nowrap;
          color: ${({ theme }) => theme.colors.darkText};

          &.is-disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          &:hover,
          &:focus {
            background-color: ${({ theme }) => theme.colors.primary};
            color: #fff;

            &.is-disabled {
              background-color: #444;
            }
          }
        }
      }
    }
  }
`

export interface ContextMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  functionalities: Functionality[]
  xBasis: 'left' | 'right'
  yBasis: 'top' | 'bottom'
  xBasisOffset?: number
  yBasisOffset?: number
  onExit?: () => void
}

function ContextMenu({
  functionalities,
  xBasis,
  yBasis,
  xBasisOffset = 0,
  yBasisOffset = 0,
  onExit,
}: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null)
  useClickAway(ref, () => (onExit ? onExit() : undefined))
  useKey('Escape', onExit)

  return (
    <ContextMenuWrapper
      ref={ref}
      style={{
        [xBasis]: xBasisOffset,
        [yBasis]: yBasisOffset,
      }}
    >
      <div className='relative'>
        <ul>
          {functionalities.map((functionality, index) => (
            <li key={index}>
              <a
                href='javascript:;'
                onClick={event => {
                  event.preventDefault()
                  if (functionality.isDisabled) return
                  const promise = functionality.run()
                  if (promise && onExit) {
                    promise.finally(onExit)
                  } else if (onExit) {
                    onExit()
                  }
                }}
                className={functionality.isDisabled ? 'is-disabled' : undefined}
              >
                {functionality.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </ContextMenuWrapper>
  )
}
export default memo(ContextMenu)
